import {useMutation, useQueryClient} from '@tanstack/react-query'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {httpUpdateFreelancerAddress, HttpUpdateFreelancerAddressPayload} from '@features/settings/http/settings.http.ts'
import {mapPhpPhotographerGeos, PhpUser, User} from '@features/user/types.ts'
import {captureException} from '@sentry/react'
import {errorHandler, raise} from '@utilities/helpers.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {z} from 'zod'

const ResponseShape = PhpUser.pick({
    address: true,
    photographer_geos: true,
    longitude: true,
    latitude: true,
    last_markets_updated_at: true
})
type ResponseShape = z.infer<typeof ResponseShape>

export const useChangeFreelancerAddress = ({userId, onSuccess}: {userId?: number; onSuccess?: () => void}) => {
    const queryClient = useQueryClient()

    return useMutation<ResponseShape, unknown, HttpUpdateFreelancerAddressPayload>({
        mutationFn: payload =>
            parseAxiosPromise({
                axiosPromise: httpUpdateFreelancerAddress({
                    payload,
                    urlParams: {userId: userId ?? raise('userId is required')},
                    params: {response_type: 'extended'}
                }),
                responseShape: ResponseShape,
                onZodError: captureException
            }),
        onError: errorHandler,
        onSuccess: async data => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})
            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (previousData: User): User => ({
                    ...previousData,
                    address: data.address,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    photographerGeos: mapPhpPhotographerGeos(data.photographer_geos),
                    lastMarketsUpdatedAt: data.last_markets_updated_at
                })
            )
            onSuccess?.()
        }
    })
}
